/**
 * Inline Annotation Plugin for Lexical
 * Renders inline range annotations as a side indicator beside the text. The transparent
 * text-shaped overlay tracks the selection so we can show a dashed hover outline when
 * the user moves the mouse over the marked text. Only visible to authors in the editor.
 */

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { type TagDefinition } from '@shared/tags/tagRegistry'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import { ContentAST, getBlockText } from '../../../../transforms/ast'
import { Annotation, isInlineRangeAnchor } from '../../../../types/annotations'

/** First tag color defined on this annotation, or null. */
function findAnnotationColor(annotation: Annotation, tagRegistry: Record<string, TagDefinition>): string | null {
  for (const tagId of annotation.tags) {
    const color = tagRegistry[tagId]?.color
    if (color) {
      return color
    }
  }
  return null
}

export interface InlineAnnotationHoverInfo {
  annotationId: string
  tags: string[]
  position: { top: number; left: number | null; right: number | null }
}

export interface InlineAnnotationPluginProps {
  /** Annotations for the chapter */
  annotations?: Annotation[]
  /** AST for offset calculations */
  ast?: ContentAST
  /** Whether the current user is an author */
  isAuthor?: boolean
  /** Tag registry — used to look up each tag's `color` for the side indicator tint. */
  tagRegistry?: Record<string, TagDefinition>
  /** Callback when an inline annotation is clicked */
  onAnnotationClick?: (annotationId: string) => void
  /** Callback when an inline annotation is hovered */
  onInlineHover?: (info: InlineAnnotationHoverInfo | null) => void
}

// CSS class for inline annotations
const INLINE_ANNOTATION_CLASS = 'lexical-inline-annotation'

// Inject styles once
let stylesInjected = false
function injectStyles() {
  if (stylesInjected) {
    return
  }
  stylesInjected = true

  // Colors are driven by `--tag-color` set inline per highlight/side div (see applyHighlights);
  // see TagIndicatorPlugin for the same color-mix pattern.
  const style = document.createElement('style')
  style.textContent = `
    /* Inline annotation - transparent at rest, dashed hover outline only */
    .${INLINE_ANNOTATION_CLASS} {
      pointer-events: none;
      border-radius: 2px;
    }
    /* Side indicator for inline annotations */
    .${INLINE_ANNOTATION_CLASS}-side {
      position: absolute;
      width: 3px;
      background-color: color-mix(in srgb, var(--tag-color, #2196F3) 55%, transparent);
      border-radius: 2px;
      pointer-events: none;
      z-index: 1;
    }
    /* Hover state for inline annotations */
    .${INLINE_ANNOTATION_CLASS}.hovered {
      background-color: color-mix(in srgb, var(--tag-color, #2196F3) 8%, transparent);
      outline: 1px dashed color-mix(in srgb, var(--tag-color, #2196F3) 40%, transparent);
      outline-offset: 1px;
    }
    .${INLINE_ANNOTATION_CLASS}-side.hovered {
      background-color: color-mix(in srgb, var(--tag-color, #2196F3) 75%, transparent);
    }
  `
  document.head.appendChild(style)
}

/** A per-block slice of an inline-range annotation. */
interface HighlightRange {
  annotationId: string
  blockId: string
  startOffset: number
  endOffset: number
}

function extractHighlightRanges(annotations: Annotation[], ast: ContentAST): HighlightRange[] {
  const ranges: HighlightRange[] = []

  for (const annotation of annotations) {
    const anchor = annotation.anchor
    if (!isInlineRangeAnchor(anchor)) {
      continue
    }

    const { startBlockId, startOffset, endBlockId, endOffset } = anchor

    const normalizedStartOffset = Math.min(startOffset, endOffset)
    const normalizedEndOffset = Math.max(startOffset, endOffset)

    if (startBlockId === endBlockId) {
      ranges.push({
        annotationId: annotation.id,
        blockId: startBlockId,
        startOffset: normalizedStartOffset,
        endOffset: normalizedEndOffset,
      })
      continue
    }

    const startIndex = ast.blocks.findIndex((b) => b.id === startBlockId)
    const endIndex = ast.blocks.findIndex((b) => b.id === endBlockId)

    if (startIndex === -1 || endIndex === -1) {
      continue
    }

    const actualStartIndex = Math.min(startIndex, endIndex)
    const actualEndIndex = Math.max(startIndex, endIndex)

    for (let i = actualStartIndex; i <= actualEndIndex; i++) {
      const block = ast.blocks[i]
      const blockText = getBlockText(block)

      if (i === actualStartIndex) {
        ranges.push({
          annotationId: annotation.id,
          blockId: block.id,
          startOffset: normalizedStartOffset,
          endOffset: blockText.length,
        })
      } else if (i === actualEndIndex) {
        ranges.push({
          annotationId: annotation.id,
          blockId: block.id,
          startOffset: 0,
          endOffset: normalizedEndOffset,
        })
      } else {
        ranges.push({
          annotationId: annotation.id,
          blockId: block.id,
          startOffset: 0,
          endOffset: blockText.length,
        })
      }
    }
  }

  return ranges
}

function groupRangesByBlock(ranges: HighlightRange[]): Map<string, HighlightRange[]> {
  const grouped = new Map<string, HighlightRange[]>()

  for (const range of ranges) {
    const existing = grouped.get(range.blockId) || []
    existing.push(range)
    grouped.set(range.blockId, existing)
  }

  return grouped
}

export function InlineAnnotationPlugin({
  annotations = [],
  ast,
  isAuthor = false,
  tagRegistry = {},
  onAnnotationClick: _onAnnotationClick,
  onInlineHover,
}: InlineAnnotationPluginProps) {
  const [editor] = useLexicalComposerContext()
  const highlightElementsRef = useRef<HTMLElement[]>([])

  const highlightRanges = useMemo(() => {
    if (!ast) {
      return []
    }
    return extractHighlightRanges(annotations, ast)
  }, [annotations, ast])

  const rangesByBlock = useMemo(() => groupRangesByBlock(highlightRanges), [highlightRanges])

  // annotationId → hex color (if the annotation carries a colored tag). Used to drive
  // the --tag-color CSS variable on each overlay/side div for the bar tint.
  const annotationColors = useMemo(() => {
    const colors = new Map<string, string>()
    for (const annotation of annotations) {
      const color = findAnnotationColor(annotation, tagRegistry)
      if (color) {
        colors.set(annotation.id, color)
      }
    }
    return colors
  }, [annotations, tagRegistry])

  const cleanupHighlights = useCallback(() => {
    highlightElementsRef.current.forEach((el) => {
      el.remove()
    })
    highlightElementsRef.current = []
  }, [])

  // Maps annotation id → its rendered DOM elements, used by the hover handler.
  const inlineAnnotationMapRef = useRef<
    Map<
      string,
      {
        highlights: HTMLElement[]
        sideIndicators: HTMLElement[]
      }
    >
  >(new Map())

  const applyHighlights = useCallback(() => {
    if (!isAuthor || !ast) {
      return
    }

    cleanupHighlights()
    inlineAnnotationMapRef.current.clear()

    if (rangesByBlock.size === 0) {
      return
    }

    const rootElement = editor.getRootElement()
    const parentBox = rootElement?.parentElement
    if (!rootElement || !parentBox) {
      return
    }

    if (!parentBox.style.position || parentBox.style.position === 'static') {
      parentBox.style.position = 'relative'
    }

    const parentRect = parentBox.getBoundingClientRect()
    const direction = ast.meta.dir || 'ltr'
    const isRTL = direction === 'rtl'

    type Visual = {
      highlights: HTMLElement[]
      minTop: number
      maxBottom: number
      left: number | null
      right: number | null
    }
    const annotationVisuals = new Map<string, Visual>()

    rangesByBlock.forEach((ranges, blockId) => {
      const blockElement = rootElement.querySelector(`[data-block-id="${blockId}"]`) as HTMLElement
      if (!blockElement) {
        return
      }

      const walker = document.createTreeWalker(blockElement, NodeFilter.SHOW_TEXT, null)
      const textNodes: Array<{ node: Text; start: number; end: number }> = []
      let currentOffset = 0
      let textNode: Text | null

      while ((textNode = walker.nextNode() as Text | null)) {
        const nodeLength = textNode.length
        textNodes.push({
          node: textNode,
          start: currentOffset,
          end: currentOffset + nodeLength,
        })
        currentOffset += nodeLength
      }

      const blockRect = blockElement.getBoundingClientRect()
      const sideX = isRTL ? blockRect.right - parentRect.left + 12 : blockRect.left - parentRect.left - 12 - 3

      for (const range of ranges) {
        let minTop = Infinity
        let maxBottom = 0
        const highlights: HTMLElement[] = []

        for (const textSegment of textNodes) {
          const nodeStart = textSegment.start
          const nodeEnd = textSegment.end

          const highlightStart = Math.max(range.startOffset, nodeStart)
          const highlightEnd = Math.min(range.endOffset, nodeEnd)

          if (highlightStart < highlightEnd) {
            const localStart = highlightStart - nodeStart
            const localEnd = highlightEnd - nodeStart

            try {
              const domRange = document.createRange()
              domRange.setStart(textSegment.node, localStart)
              domRange.setEnd(textSegment.node, localEnd)

              const rangeRects = domRange.getClientRects()

              const rangeColor = annotationColors.get(range.annotationId)
              for (const rect of rangeRects) {
                const highlight = document.createElement('div')
                highlight.className = INLINE_ANNOTATION_CLASS
                highlight.dataset.annotationId = range.annotationId
                highlight.style.position = 'absolute'
                const top = rect.top - parentRect.top + parentBox.scrollTop
                highlight.style.top = `${top}px`
                highlight.style.left = `${rect.left - parentRect.left}px`
                highlight.style.width = `${rect.width}px`
                highlight.style.height = `${rect.height}px`
                highlight.style.zIndex = '0'
                if (rangeColor) {
                  highlight.style.setProperty('--tag-color', rangeColor)
                }

                parentBox.appendChild(highlight)
                highlightElementsRef.current.push(highlight)
                highlights.push(highlight)

                minTop = Math.min(minTop, top)
                maxBottom = Math.max(maxBottom, top + rect.height)
              }
            } catch {
              // Range measurement failed - silently skip
            }
          }
        }

        if (highlights.length > 0 && minTop !== Infinity) {
          const existing = annotationVisuals.get(range.annotationId)

          if (existing) {
            existing.highlights.push(...highlights)
            existing.minTop = Math.min(existing.minTop, minTop)
            existing.maxBottom = Math.max(existing.maxBottom, maxBottom)
          } else {
            annotationVisuals.set(range.annotationId, {
              highlights: [...highlights],
              minTop,
              maxBottom,
              left: isRTL ? null : sideX,
              right: isRTL ? parentRect.right - blockRect.right - 12 - 3 : null,
            })
          }
        }
      }
    })

    for (const [annotationId, visual] of annotationVisuals) {
      const sideIndicator = document.createElement('div')
      sideIndicator.className = `${INLINE_ANNOTATION_CLASS}-side`
      sideIndicator.dataset.annotationId = annotationId
      sideIndicator.style.top = `${visual.minTop}px`
      sideIndicator.style.height = `${Math.max(visual.maxBottom - visual.minTop, 0)}px`

      if (visual.right != null) {
        sideIndicator.style.right = `${visual.right}px`
        sideIndicator.style.left = 'auto'
      } else if (visual.left != null) {
        sideIndicator.style.left = `${visual.left}px`
        sideIndicator.style.right = 'auto'
      }

      const annotationColor = annotationColors.get(annotationId)
      if (annotationColor) {
        sideIndicator.style.setProperty('--tag-color', annotationColor)
      }

      parentBox.appendChild(sideIndicator)
      highlightElementsRef.current.push(sideIndicator)
      inlineAnnotationMapRef.current.set(annotationId, {
        highlights: visual.highlights,
        sideIndicators: [sideIndicator],
      })
    }
  }, [editor, isAuthor, ast, rangesByBlock, cleanupHighlights, annotationColors])

  useEffect(() => {
    if (isAuthor) {
      injectStyles()
    }
  }, [isAuthor])

  // Rebuild overlays when annotations / AST change AND on every Lexical update — needed
  // so the overlays follow text wraps as the user types. We coalesce per-keystroke
  // triggers with a single rAF latch so rapid typing collapses to at most one rebuild
  // per frame. When there are no inline-range annotations, `applyHighlights` early-exits
  // and the per-keystroke cost is essentially zero.
  useEffect(() => {
    if (!isAuthor) {
      cleanupHighlights()
      return
    }

    let pendingRaf: number | null = null
    const schedule = () => {
      if (pendingRaf !== null) {
        return
      }
      pendingRaf = requestAnimationFrame(() => {
        pendingRaf = null
        applyHighlights()
      })
    }

    schedule()
    const unregister = editor.registerUpdateListener(schedule)

    return () => {
      if (pendingRaf !== null) {
        cancelAnimationFrame(pendingRaf)
      }
      unregister()
    }
  }, [editor, isAuthor, applyHighlights, cleanupHighlights])

  useEffect(() => {
    if (!isAuthor) {
      return
    }

    const rootElement = editor.getRootElement()
    const parentBox = rootElement?.parentElement
    if (!rootElement || !parentBox) {
      return
    }

    let hoveredAnnotationId: string | null = null

    const clearHover = () => {
      if (hoveredAnnotationId) {
        const entry = inlineAnnotationMapRef.current.get(hoveredAnnotationId)
        if (entry) {
          entry.highlights.forEach((h) => h.classList.remove('hovered'))
          entry.sideIndicators.forEach((indicator) => indicator.classList.remove('hovered'))
        }
        hoveredAnnotationId = null
        onInlineHover?.(null)
      }
    }

    const handleMouseMove = (e: MouseEvent) => {
      let foundAnnotationId: string | null = null
      let foundEntry: { highlights: HTMLElement[]; sideIndicators: HTMLElement[] } | null = null

      for (const [annotationId, entry] of inlineAnnotationMapRef.current) {
        for (const highlight of entry.highlights) {
          const rect = highlight.getBoundingClientRect()
          if (e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom) {
            foundAnnotationId = annotationId
            foundEntry = entry
            break
          }
        }
        if (foundAnnotationId) {
          break
        }
      }

      if (foundAnnotationId !== hoveredAnnotationId) {
        clearHover()
        if (foundAnnotationId && foundEntry) {
          hoveredAnnotationId = foundAnnotationId
          foundEntry.highlights.forEach((h) => h.classList.add('hovered'))
          foundEntry.sideIndicators.forEach((indicator) => indicator.classList.add('hovered'))

          const annotation = annotations.find((a) => a.id === foundAnnotationId)
          const activeSideIndicator =
            foundEntry.sideIndicators.find((indicator) => {
              const rect = indicator.getBoundingClientRect()
              return e.clientY >= rect.top && e.clientY <= rect.bottom
            }) || foundEntry.sideIndicators[0]

          if (annotation && activeSideIndicator) {
            const parentRect = parentBox.getBoundingClientRect()
            const direction = ast?.meta.dir || 'ltr'
            const isRTL = direction === 'rtl'
            const sideRect = activeSideIndicator.getBoundingClientRect()

            onInlineHover?.({
              annotationId: foundAnnotationId,
              tags: annotation.tags,
              position: {
                top: sideRect.top - parentRect.top + parentBox.scrollTop,
                left: isRTL ? null : sideRect.left - parentRect.left - 120,
                right: isRTL ? parentRect.right - sideRect.right - 120 : null,
              },
            })
          }
        }
      }
    }

    const handleMouseLeave = () => {
      clearHover()
    }

    rootElement.addEventListener('mousemove', handleMouseMove)
    rootElement.addEventListener('mouseleave', handleMouseLeave)

    return () => {
      rootElement.removeEventListener('mousemove', handleMouseMove)
      rootElement.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [editor, isAuthor, annotations, ast, onInlineHover])

  useEffect(() => {
    return () => {
      cleanupHighlights()
    }
  }, [cleanupHighlights])

  return null
}
